import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowUpRight } from "lucide-react";
import Logo from "@/components/site/Logo";
import { CONTACT } from "@/lib/contact";

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer
      data-testid="site-footer"
      className="bg-[#2B2420] text-white pt-20 md:pt-24 pb-8 relative overflow-hidden"
    >
      <div className="px-6 md:px-10 lg:px-16">
        {/* CTA row */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 border-b border-white/10 pb-14 md:pb-16">
          <div className="md:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.3em] text-white/60 mb-3">
              Let's build something
            </div>
            <h3
              className="font-black tracking-tighter text-3xl md:text-4xl lg:text-5xl leading-[1]"
              style={{ fontFamily: "Cabinet Grotesk" }}
            >
              Free quote, quick <br /> response — today.
            </h3>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <a
                href="#quote"
                data-testid="footer-cta-quote"
                className="inline-flex items-center gap-2 bg-[#C9A24C] hover:bg-[#B08C3F] text-[#2B2420] px-6 py-4 text-sm font-bold uppercase tracking-[0.15em] transition-colors"
              >
                Request a Free Quote <ArrowUpRight className="w-4 h-4" />
              </a>
              <a
                href={`tel:${CONTACT.primary.tel}`}
                data-testid="footer-cta-call"
                className="inline-flex items-center gap-2 border border-white/40 hover:border-white hover:bg-white hover:text-[#2B2420] text-white px-6 py-4 text-sm font-bold uppercase tracking-[0.15em] transition-colors"
              >
                <Phone className="w-4 h-4" /> {CONTACT.primary.display}
              </a>
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="text-xs uppercase tracking-[0.3em] text-white/50 mb-5">Contact</div>
            <ul className="space-y-3 text-sm">
              <li>
                <a href={`tel:${CONTACT.primary.tel}`} className="flex items-start gap-2 hover:text-[#C9A24C]" data-testid="footer-phone-primary">
                  <Phone className="w-4 h-4 mt-0.5 text-[#C9A24C]" />
                  <span>
                    <span className="font-bold block">{CONTACT.primary.display}</span>
                    <span className="text-white/60 text-xs">{CONTACT.primary.name}</span>
                  </span>
                </a>
              </li>
              <li>
                <a href={`tel:${CONTACT.secondary.tel}`} className="flex items-start gap-2 hover:text-[#C9A24C]" data-testid="footer-phone-secondary">
                  <Phone className="w-4 h-4 mt-0.5 text-[#C9A24C]" />
                  <span>
                    <span className="font-bold block">{CONTACT.secondary.display}</span>
                    <span className="text-white/60 text-xs">{CONTACT.secondary.name}</span>
                  </span>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${CONTACT.email}`}
                  data-testid="footer-email"
                  className="flex items-start gap-2 hover:text-[#C9A24C] break-all"
                >
                  <Mail className="w-4 h-4 mt-0.5 text-[#C9A24C]" />
                  {CONTACT.email}
                </a>
              </li>
              <li className="flex items-start gap-2 text-white/80" data-testid="footer-address">
                <MapPin className="w-4 h-4 mt-0.5 text-[#C9A24C]" />
                <address className="not-italic text-sm leading-relaxed">
                  {CONTACT.address.line1}<br />
                  {CONTACT.address.line2}<br />
                  {CONTACT.address.city}<br />
                  {CONTACT.address.postcode}
                </address>
              </li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="text-xs uppercase tracking-[0.3em] text-white/50 mb-5">Services</div>
            <ul className="space-y-2 text-sm text-white/80">
              <li>Commercial Fit-Out</li>
              <li>Acoustic Solutions</li>
              <li>Partitions &amp; Drylining</li>
              <li>Suspended Ceilings</li>
              <li>Fire Door Installation</li>
              <li>Painting &amp; Finishing</li>
              <li>
                <Link to="/demolition-strip-out-london" className="hover:text-[#C9A24C]">
                  Demolition &amp; Strip-Out
                </Link>
              </li>
              <li>Plastering</li>
              <li>Flooring &amp; Tiling</li>
              <li>Carpentry &amp; Doors</li>
              <li>
                <Link to="/services" className="underline hover:text-[#C9A24C]">
                  All services →
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="text-xs uppercase tracking-[0.3em] text-white/50 mb-5">Company</div>
            <ul className="space-y-2 text-sm text-white/80" data-testid="footer-company-details">
              <li>
                <span className="block text-white/50 text-[11px] uppercase tracking-wider">Co. No.</span>
                <span className="font-bold">{CONTACT.legal.companyNumber}</span>
              </li>
              <li>
                <span className="block text-white/50 text-[11px] uppercase tracking-wider">VAT</span>
                <span className="font-bold">{CONTACT.legal.vatNumber}</span>
              </li>
              <li>
                <span className="block text-white/50 text-[11px] uppercase tracking-wider">UTR</span>
                <span className="font-bold">{CONTACT.legal.utrNumber}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Oversized brand with logo */}
        <div className="pt-14 pb-8 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <Logo variant="light" size="lg" />
          <div className="text-xs uppercase tracking-[0.3em] text-[#C9A24C]">
            Construction &amp; Refurbishment · London
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-white/50 border-t border-white/10 pt-6">
          <div>© {year} CONSTANTINOV LTD. All rights reserved.</div>
          <div className="flex gap-5">
            <a href="#" className="hover:text-white">Privacy</a>
            <a href="#" className="hover:text-white">Terms</a>
            <a href="#" className="hover:text-white">Cookies</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
